// apps/client/src/hooks/useLoanBookView.ts
//
// State machine for the scoped loan book panel (Phases 3 and 6 of
// docs/handovers/2026-08-04-LOAN-BOOK-VS-LMG-RECONCILIATION.md).
//
// DELIBERATELY TRANSPORT-FREE. The two real fetchers live in
// `loanBookViewFetchers.ts`, because naming a callable means importing
// `../lib/firebase`, which initialises a real Firebase app at module load and
// would make this module unimportable from a hermetic unit test.
//
// BACKEND (wired 2026-08-04). The contract is produced by `getBrokerReport` and
// `getSharedBrokerReport` themselves, not by a separate callable, so:
//
//   - the FIRST view costs no extra round trip. The page passes the view that
//     came back with the report as `initialView`, and the hook serves it.
//   - only a MODE CHANGE fetches.
//   - the fail-safe is load-bearing: a missing `loanBookView`, or any thrown
//     call, resolves to `null`, and a `null` view makes the report fall back to
//     its existing single "Loan Book Value" card rather than breaking the page.
//
// No figure is ever invented. Showing a fabricated split on a report that is
// used for staff performance reviews is exactly the failure this work exists to
// prevent.

import { useCallback, useEffect, useState } from 'react';
import type { LoanBookMode, LoanBookView } from '../types/loanBookView';

export interface LoanBookViewRequest {
  companyId: string;
  brokerDisplay: string;
  mode: LoanBookMode;
  /** Present on the public shared report, where there is no signed-in user. */
  shareToken?: string;
}

/** Resolves to `null` when the scoped view cannot be produced for this caller. */
export type LoanBookViewFetcher = (req: LoanBookViewRequest) => Promise<LoanBookView | null>;

/**
 * Fetcher of last resort. Reports "not available", which is the fail-safe every
 * consumer is built around. Still the DEFAULT, so a caller that forgets to pass
 * a fetcher degrades to the legacy card rather than to a wrong number.
 */
export const notAvailableFetcher: LoanBookViewFetcher = async () => null;

export interface UseLoanBookViewResult {
  view: LoanBookView | null;
  loading: boolean;
  error: string | null;
  /** Currently requested mode. Optimistic: flips immediately, data follows. */
  mode: LoanBookMode;
  setMode: (mode: LoanBookMode) => void;
}

export function useLoanBookView(
  req: Omit<LoanBookViewRequest, 'mode'>,
  options?: {
    fetcher?: LoanBookViewFetcher;
    initialMode?: LoanBookMode;
    enabled?: boolean;
    /**
     * The view that already arrived with the report payload, for the INITIAL
     * mode. Supplying it means the first render costs no extra round trip and
     * only a scope change fetches. `null` is a meaningful value ("the backend
     * returned no view"); `undefined` means "nothing supplied, go and fetch".
     */
    initialView?: LoanBookView | null;
  },
): UseLoanBookViewResult {
  const fetcher = options?.fetcher ?? notAvailableFetcher;
  const enabled = options?.enabled ?? true;
  const initialMode: LoanBookMode = options?.initialMode ?? 'primary';
  const initialView = options?.initialView;

  const [mode, setMode] = useState<LoanBookMode>(initialMode);
  const [view, setView] = useState<LoanBookView | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);

  const { companyId, brokerDisplay, shareToken } = req;

  const load = useCallback(
    async (signal: { cancelled: boolean }) => {
      if (!enabled) {
        setLoading(false);
        return;
      }
      // The report already carried the view for its own mode, so serve that
      // rather than asking the backend for something it just sent.
      if (initialView !== undefined && mode === initialMode) {
        setView(initialView);
        setError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const next = await fetcher({ companyId, brokerDisplay, mode, shareToken });
        if (signal.cancelled) return;
        setView(next);
      } catch (err) {
        if (signal.cancelled) return;
        // Fail safe: no view means the report falls back to its legacy card.
        console.error('Failed to load loan book view:', err);
        setView(null);
        setError(err instanceof Error ? err.message : 'Failed to load loan book view');
      } finally {
        if (!signal.cancelled) setLoading(false);
      }
    },
    [fetcher, companyId, brokerDisplay, mode, shareToken, enabled, initialView, initialMode],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load]);

  return { view, loading, error, mode, setMode };
}
